import {
    BsdfPrincipled,
    Color,
    isColor,
    isVec3,
    MaterialConnection,
    MaterialNode,
    TextureFromRevision,
    TextureFromSet,
    TextureSet,
    TextureType,
} from "@cm/material-nodes"
import type {MaterialGraph} from "@cm/material-nodes"
import {
    chainInputSocket,
    isProceduralTextureNode,
    isSupportedChainAdapterNode,
    isSupportedLinearImageOperatorSequenceNode,
} from "#material-maps-exporter/core/types"

export class MaterialGraphUnsupportedError extends Error {
    readonly nodeClass: string | undefined
    /** Populated on the refusal raised for the BSDF slots as a whole; empty on a throw from a single site. */
    readonly issues: MaterialGraphIssue[]

    constructor(message: string, nodeClass?: string, issues: MaterialGraphIssue[] = []) {
        super(message)
        this.name = "MaterialGraphUnsupportedError"
        this.nodeClass = nodeClass
        this.issues = issues
    }
}

/** A graph the exporter cannot even walk: an invariant violation, not something an author did. */
export class MaterialGraphInvariantError extends MaterialGraphUnsupportedError {}

/**
 * A chain the exporter has nothing to bake, rather than one it cannot read — procedural content is a
 * legitimate way to author a material. Thrown to unwind the walk like any other, but whether it
 * refuses an export is the channel's call, not this throw's: see `CHANNELS_AN_EXPORT_CAN_OMIT`.
 */
export class MaterialGraphNothingToBakeError extends MaterialGraphUnsupportedError {}

/**
 * `shape` is decidable from the graph alone — the editor can check it live. `data` only surfaces once
 * the referenced texture sets and revisions are resolved, so it needs the server.
 */
export type MaterialGraphIssueCategory = "shape" | "data"

/**
 * `blocking` refuses the export: the exporter cannot produce a correct result for this channel.
 * `advisory` warns only — the export stays valid and simply carries nothing for what the issue names.
 */
export type MaterialGraphIssueSeverity = "blocking" | "advisory"

export type MaterialGraphIssue = {
    bsdfInput: string
    /** The same slot as the author reads it on the BSDF, so every consumer names it one way. */
    bsdfInputLabel: string
    nodeClass: string | undefined
    category: MaterialGraphIssueCategory
    severity: MaterialGraphIssueSeverity
    message: string
    /** Kept in the export's refusal, hidden from the editor's warnings — the author cannot act on it. */
    internal?: boolean
}

const nodeLabel = (node: MaterialNode): string => node.definition.ui?.label ?? node.nodeClass

const inputSocketLabel = (node: MaterialNode, socketName: string): string => {
    const item = node.definition.ui?.items.find((item) => !item.custom && item.name === socketName && item.input)
    return item !== undefined && !item.custom ? item.label : socketName
}

/**
 * Whether a socket's value may come from the node's settings when nothing is connected. Decided from
 * the definition, because that is what decides it for the editor too: a socket the editor offers no
 * field for still carries a value under `settings`, and reading it would invent a map the author never
 * set — a normal of `{0, 0, 0}` exporting as a black map rather than as no normal map at all.
 */
const socketCarriesSettingValue = (node: MaterialNode, socketName: string): boolean =>
    node.definition.ui?.items.some((item) => !item.custom && item.name === socketName && item.setting) ?? false

/** The label the author reads on the channel's socket, so a warning and a refusal name it the same way. */
export const bsdfInputLabel = (node: MaterialNode, socketName: string): string => inputSocketLabel(node, socketName)

const connectedInputLabelsOtherThan = (node: MaterialNode, chainInput: string): string[] =>
    Object.keys(node.parameters.inputs ?? {})
        .filter((socketName) => socketName !== chainInput)
        .map((socketName) => inputSocketLabel(node, socketName))

const joinAnd = (labels: string[]): string => (labels.length <= 1 ? (labels[0] ?? "") : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`)

export type BsdfInput = "alpha" | "baseColor" | "metallic" | "normal" | "roughness" | "specular"

/**
 * Everything the exporter reads out of a graph. All but `displacement` are inputs on the Principled
 * BSDF; displacement is read off the Material Output, because that is where the author wires it.
 */
export type ExportChannel = BsdfInput | "displacement"

/** The BSDF inputs the exporter reads, each with the texture type it feeds, in the order channels are reported. */
export const BSDF_INPUT_TEXTURE_TYPES: readonly (readonly [BsdfInput, TextureType])[] = [
    ["baseColor", TextureType.Diffuse],
    ["metallic", TextureType.Metalness],
    ["specular", TextureType.SpecularStrength],
    ["roughness", TextureType.Roughness],
    ["normal", TextureType.Normal],
    ["alpha", TextureType.Mask],
]

/** The Material Output socket the exporter reads, and the texture type it feeds. Reported after the BSDF's. */
export const OUTPUT_CHANNEL_TEXTURE_TYPE = ["displacement", TextureType.Displacement] as const satisfies readonly [ExportChannel, TextureType]

/** The node a slot's chain starts from: a texture reference to resolve, or a constant colour. */
export type SlotHead = TextureFromRevision | TextureSet | TextureFromSet | Color

export const isSlotHead = (node: MaterialNode): node is SlotHead =>
    node instanceof TextureFromRevision || node instanceof TextureSet || node instanceof TextureFromSet || node instanceof Color

export type SlotAnalysis =
    /** Neither a connection nor a setting — an expected miss, which the gather's backstop may still fill. */
    | {kind: "unconnected"}
    /**
     * A linear chain the exporter can read: its head, then the transforms applied to it in order.
     * `advisories` are notes about a chain that exports fine but may not be what the author meant.
     */
    | {kind: "chain"; head: SlotHead; transformations: MaterialNode[]; advisories: MaterialGraphIssue[]}
    | {kind: "unsupported"; issue: MaterialGraphIssue}

export type SlotAnalysisEntry = {
    bsdfInput: ExportChannel
    /** Carried here rather than re-derived: only the analysis knows which node owns the channel. */
    bsdfInputLabel: string
    textureType: TextureType
    analysis: SlotAnalysis
}

export type MaterialGraphAnalysis = {kind: "noBsdf"; bsdfCount: number} | {kind: "bsdf"; bsdf: BsdfPrincipled; slots: SlotAnalysisEntry[]}

/**
 * Why a chain head cannot be resolved as it stands, or `undefined` when its settings are complete.
 * Shared with the resolvers so Phase 1 and Phase 2 refuse an unconfigured texture node with one message.
 */
export const incompleteHeadMessage = (node: SlotHead): string | undefined => {
    if (node instanceof TextureFromRevision) {
        return node.parameters.settings.textureRevisionLegacyId ? undefined : `${nodeLabel(node)}: no texture is selected.`
    }
    if (node instanceof TextureFromSet && !node.mapAssignmentTextureType) {
        return `${nodeLabel(node)}: no map is selected.`
    }
    if (node instanceof TextureSet || node instanceof TextureFromSet) {
        return node.parameters.settings.textureSetRevisionId ? undefined : `${nodeLabel(node)}: no texture set is selected.`
    }
    return undefined
}

const parameterToShaderNodeRGB = (parameter: unknown): Color => {
    if (parameter instanceof Color) return parameter
    else if (isColor(parameter)) return new Color({parameters: {inputs: {}, settings: {color: parameter}}})
    else if (typeof parameter === "number") {
        return new Color({
            parameters: {
                inputs: {},
                settings: {
                    color: {r: parameter, g: parameter, b: parameter},
                },
            },
        })
    } else if (isVec3(parameter)) {
        return new Color({
            parameters: {
                inputs: {},
                settings: {
                    color: {r: parameter.x, g: parameter.y, b: parameter.z},
                },
            },
        })
    } else throw new MaterialGraphUnsupportedError("The value set here is neither a number nor a colour, so the exporter cannot read it.")
}

const collectLinearImageOperatorSequence = (node: unknown) => {
    const linearTransforms: MaterialNode[] = []

    const traverse = (node: unknown) => {
        if (node instanceof MaterialConnection) {
            traverse(node.source)
            return
        }
        if (!(node instanceof MaterialNode)) throw new MaterialGraphInvariantError(`Encountered a graph entry that is not a material node`)
        if (!isSupportedLinearImageOperatorSequenceNode(node)) {
            if (isProceduralTextureNode(node))
                throw new MaterialGraphNothingToBakeError(
                    `${nodeLabel(node)} generates its image procedurally, so the exporter has no map to read from this chain.`,
                    node.nodeClass,
                )
            throw new MaterialGraphUnsupportedError(`${nodeLabel(node)} is not a node the exporter can read.`, node.nodeClass)
        }
        // An adapter is walked through, not applied — collecting it would hand the image pipeline a
        // node it has no transform for.
        if (!isSupportedChainAdapterNode(node)) linearTransforms.push(node)
        if (node instanceof TextureFromRevision || node instanceof TextureSet || node instanceof TextureFromSet) return
        const chainInput = chainInputSocket(node)
        // Looked up by the socket name the node itself names, which the narrowed key union cannot express.
        const inputs = (node.parameters.inputs ?? {}) as Record<string, unknown>
        // An adapter forwards nothing unless its chain input is wired, and it was not collected above,
        // so without this the sequence would come back empty and read as an internal fault.
        if (isSupportedChainAdapterNode(node) && !(chainInput in inputs))
            throw new MaterialGraphUnsupportedError(
                `${nodeLabel(node)}: nothing is connected to the ${inputSocketLabel(node, chainInput)} input.`,
                node.nodeClass,
            )
        if (Object.keys(inputs).length > 0) {
            const otherInputLabels = connectedInputLabelsOtherThan(node, chainInput)
            const otherInputs = joinAnd(otherInputLabels)
            if (Object.keys(inputs).length !== 1)
                throw new MaterialGraphUnsupportedError(
                    `${nodeLabel(node)}: the exporter follows only the ${inputSocketLabel(node, chainInput)} input. ${otherInputs} ${
                        otherInputLabels.length > 1 ? "are" : "is"
                    } also connected.`,
                    node.nodeClass,
                )
            // Only a transform reaches this: `Color` declares no inputs at all, the texture heads return
            // above, and an adapter missing its chain input was refused above.
            if (!(chainInput in inputs))
                throw new MaterialGraphUnsupportedError(
                    `${nodeLabel(node)}: the exporter follows the chain through the ${inputSocketLabel(node, chainInput)} input. Here only ${otherInputs} is connected.`,
                    node.nodeClass,
                )
            traverse(inputs[chainInput])
        } else if (node instanceof Color) {
            // Just guard check, already pushed above
            if (node.parameters.settings.color === undefined)
                throw new MaterialGraphUnsupportedError(`${nodeLabel(node)}: no colour is set on this node.`, node.nodeClass)
        }
    }

    traverse(node)
    return linearTransforms.reverse()
}

const getLinearImageOperatorSequence = (node: MaterialNode, socketName: string): MaterialNode[] | undefined => {
    const inputNode = node.parameters.inputs?.[socketName]
    if (inputNode !== undefined) return collectLinearImageOperatorSequence(inputNode)
    if (!socketCarriesSettingValue(node, socketName)) return undefined
    const parameter = node.parameters.settings?.[socketName]
    return parameter === undefined ? undefined : [parameterToShaderNodeRGB(parameter)]
}

/**
 * Channels whose map encodes something other than a colour. A colour transform on one of these is
 * applied all the same — the renderer applies it too, and an export that quietly disagreed with the
 * viewport would be the worse failure — but the author is told, because it is rarely deliberate.
 */
const NON_COLOUR_CHANNELS: readonly ExportChannel[] = ["normal"]

/**
 * Channels an export can go without, because every target fills them with a sensible default. Base
 * colour is not one of them: an export with no diffuse map is black, which is nobody's material, so a
 * chain that cannot be baked there refuses the export rather than quietly shipping it.
 */
const CHANNELS_AN_EXPORT_CAN_OMIT: readonly ExportChannel[] = ["alpha", "displacement", "metallic", "normal", "roughness", "specular"]

const colourTransformAdvisories = (node: MaterialNode, bsdfInput: ExportChannel, transformations: MaterialNode[]): MaterialGraphIssue[] => {
    if (transformations.length === 0 || !NON_COLOUR_CHANNELS.includes(bsdfInput)) return []
    const labels = transformations.map(nodeLabel)
    const label = bsdfInputLabel(node, bsdfInput)
    return [
        {
            bsdfInput,
            bsdfInputLabel: label,
            nodeClass: transformations[0].nodeClass,
            category: "shape",
            severity: "advisory",
            message:
                `${joinAnd(labels)} ${labels.length > 1 ? "are" : "is"} applied to this chain. The export matches the render, ` +
                `but a colour transform changes what the ${label} map encodes.`,
        },
    ]
}

const analyzeSlot = (node: MaterialNode, bsdfInput: ExportChannel): SlotAnalysis => {
    try {
        const sequence = getLinearImageOperatorSequence(node, bsdfInput)
        if (sequence === undefined) return {kind: "unconnected"}
        const head = sequence[0]
        if (head === undefined) throw new MaterialGraphUnsupportedError(`Channel ${bsdfInput} does not have a linear image operator sequence`)
        if (!isSlotHead(head)) {
            throw new MaterialGraphUnsupportedError(
                `${nodeLabel(head)} has nothing connected to it. A chain must start at an image or a colour node.`,
                head.nodeClass,
            )
        }
        const incomplete = incompleteHeadMessage(head)
        if (incomplete !== undefined) throw new MaterialGraphUnsupportedError(incomplete, head.nodeClass)

        const transformations = sequence.slice(1)
        const misplacedNode = transformations.find(isSlotHead)
        if (misplacedNode !== undefined)
            throw new MaterialGraphUnsupportedError(
                `Channel ${bsdfInput} has a ${misplacedNode.nodeClass} node after the one feeding the chain, so the chain is not linear`,
                misplacedNode.nodeClass,
            )
        return {kind: "chain", head, transformations, advisories: colourTransformAdvisories(node, bsdfInput, transformations)}
    } catch (error) {
        if (!(error instanceof MaterialGraphUnsupportedError)) throw error
        // Nothing to bake only warns where the export can stand without the channel; anywhere else it refuses.
        const advisory = error instanceof MaterialGraphNothingToBakeError && CHANNELS_AN_EXPORT_CAN_OMIT.includes(bsdfInput)
        return {
            kind: "unsupported",
            issue: {
                bsdfInput,
                bsdfInputLabel: bsdfInputLabel(node, bsdfInput),
                nodeClass: error.nodeClass,
                category: "shape",
                severity: advisory ? "advisory" : "blocking",
                message: error.message,
                ...(error instanceof MaterialGraphInvariantError ? {internal: true} : {}),
            },
        }
    }
}

const channelEntry = (node: MaterialNode, bsdfInput: ExportChannel, textureType: TextureType): SlotAnalysisEntry => ({
    bsdfInput,
    bsdfInputLabel: bsdfInputLabel(node, bsdfInput),
    textureType,
    analysis: analyzeSlot(node, bsdfInput),
})

/**
 * What the exporter can read from a material graph, decided from the graph alone — no texture or
 * revision is resolved. Each channel is either unconnected, a linear chain from a texture or colour
 * node, or unsupported with the reason. The gather resolves the chains; the editor can show the
 * result live.
 *
 * Displacement is reported only when the graph has a Material Output to read it from. A graph
 * without one still analyses: the BSDF channels are independent of it, and a missing root is the
 * compiler's complaint, not the exporter's.
 */
export function analyzeMaterialGraph(graph: MaterialGraph): MaterialGraphAnalysis {
    const bsdfNodes = graph.nodes.filter((node) => node instanceof BsdfPrincipled)
    if (bsdfNodes.length !== 1) return {kind: "noBsdf", bsdfCount: bsdfNodes.length}
    const bsdf = bsdfNodes[0]

    const output = graph.rootNode
    const [outputChannel, outputTextureType] = OUTPUT_CHANNEL_TEXTURE_TYPE

    return {
        kind: "bsdf",
        bsdf,
        slots: [
            ...BSDF_INPUT_TEXTURE_TYPES.map(([bsdfInput, textureType]) => channelEntry(bsdf, bsdfInput, textureType)),
            ...(output ? [channelEntry(output, outputChannel, outputTextureType)] : []),
        ],
    }
}

/** Why the exporter refuses the graph as a whole, independent of any slot. */
export const noBsdfMessage = (analysis: Extract<MaterialGraphAnalysis, {kind: "noBsdf"}>): string =>
    `The exporter needs exactly one Principled BSDF node; this material has ${analysis.bsdfCount === 0 ? "none" : analysis.bsdfCount}.`

/**
 * The slots the exporter cannot read, as an author can act on them. An unconnected slot is not one of
 * them — it is an expected miss — and an invariant violation is not either.
 */
export const unreadableSlotIssues = (analysis: MaterialGraphAnalysis): MaterialGraphIssue[] =>
    analysis.kind === "noBsdf" ? [] : analysis.slots.flatMap(({analysis: slot}) => (slot.kind === "unsupported" && !slot.issue.internal ? [slot.issue] : []))

/**
 * What the exporter will do as authored, but the author probably did not mean. Kept apart from
 * `unreadableSlotIssues` because these name chains the exporter reads perfectly well: they never
 * refuse an export, and a caller that only wants reasons for a refusal should not see them.
 */
export const advisoryIssues = (analysis: MaterialGraphAnalysis): MaterialGraphIssue[] =>
    analysis.kind === "noBsdf" ? [] : analysis.slots.flatMap(({analysis: slot}) => (slot.kind === "chain" ? slot.advisories : []))
